import React, { useState } from 'react';
import { Select, mergeClasses, useId } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import './GuideComponents.css';

/** Páginas a mostrar: todas si son pocas; si no, primera, vecinas de la actual y última con "…". */
export const paginationPages = (current: number, totalPages: number): Array<number | 'gap'> => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  const pages: Array<number | 'gap'> = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(totalPages - 1, current + 1);
  if (from > 2) pages.push('gap');
  for (let page = from; page <= to; page += 1) pages.push(page);
  if (to < totalPages - 1) pages.push('gap');
  pages.push(totalPages);
  return pages;
};

export interface GuidePaginationProps {
  /** Página actual (desde 1). */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Opciones de "Filas por página"; sin `onPageSizeChange` no se muestra el selector. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

/**
 * Paginación de la grilla ("Filas por página 25", "1–5 de 60", ‹ 1 2 3 › con la activa resaltada en
 * primario tenue; alto contraste: amarillo). Va como `footer` de `GuideTable`.
 */
export const GuidePagination = ({ page, pageSize, total, onPageChange, pageSizeOptions = [10, 25, 50], onPageSizeChange, className }: GuidePaginationProps) => {
  const { t } = useTranslation();
  const sizeId = useId('guide-page-size');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(total, current * pageSize);

  return (
    <nav className={mergeClasses('ichtys-pagination', className)} aria-label={t('guideComponents.pagination.ariaLabel') as string}>
      <div className="ichtys-pagination__size">
        {onPageSizeChange && (
          <>
            <label htmlFor={sizeId}>{t('guideComponents.pagination.rowsPerPage')}</label>
            <Select id={sizeId} size="small" value={String(pageSize)} onChange={(_event, data) => onPageSizeChange(Number(data.value))}>
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </>
        )}
      </div>
      <div className="ichtys-pagination__nav">
        <span className="ichtys-pagination__range" aria-live="polite">
          {t('guideComponents.pagination.range', { from, to, total })}
        </span>
        <button
          type="button"
          className="ichtys-pagination__page ichtys-pagination__page--step"
          aria-label={t('guideComponents.pagination.previous') as string}
          disabled={current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          ‹
        </button>
        {paginationPages(current, totalPages).map((item, index) =>
          item === 'gap' ? (
            <span key={`gap-${index}`} aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className="ichtys-pagination__page"
              aria-current={item === current ? 'page' : undefined}
              aria-label={t('guideComponents.pagination.page', { page: item }) as string}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        )}
        <button
          type="button"
          className="ichtys-pagination__page ichtys-pagination__page--step"
          aria-label={t('guideComponents.pagination.next') as string}
          disabled={current >= totalPages}
          onClick={() => onPageChange(current + 1)}
        >
          ›
        </button>
      </div>
    </nav>
  );
};

/** Estado de paginación en memoria: devuelve la página de filas y las props para `GuidePagination`. */
export const useGuidePagination = <T,>(rows: T[], initialPageSize = 25) => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, totalPages);
  const pageRows = rows.slice((current - 1) * pageSize, current * pageSize);
  const paginationProps: GuidePaginationProps = {
    page: current,
    pageSize,
    total: rows.length,
    onPageChange: setPage,
    onPageSizeChange: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
  };
  return { pageRows, paginationProps };
};

export default GuidePagination;
